import { Interaction } from '../apis';
import { subscribeToBackendWrites } from './backendWrite';
import { currentDispatch } from './currentDispatch';

export const WRITE_WINDOW_MS = 150;

type PendingInteraction = {
  interaction: Interaction;
  event: Event;
  timer: ReturnType<typeof setTimeout>;
  operation: string | null;
};

export type InteractionQueue = {
  enqueue: (interaction: Interaction, event: Event) => void;
  flush: () => void;
  dispose: () => void;
};

const withWrite = (
  interaction: Interaction,
  operation: string,
): Interaction => ({
  ...interaction,
  importance: 'high',
  data: { ...interaction.data, writeOperation: operation },
});

export const createInteractionQueue = (
  emit: (interaction: Interaction) => void,
): InteractionQueue => {
  let pending: PendingInteraction | null = null;

  const release = () => {
    if (!pending) {
      return;
    }
    const entry = pending;
    pending = null;
    clearTimeout(entry.timer);
    emit(
      entry.operation
        ? withWrite(entry.interaction, entry.operation)
        : entry.interaction,
    );
  };

  const unsubscribe = subscribeToBackendWrites(operation => {
    if (!pending || pending.operation) {
      return;
    }
    const dispatch = currentDispatch();
    if (dispatch && dispatch !== pending.event) {
      return;
    }
    pending.operation = operation;
  });

  return {
    enqueue: (interaction: Interaction, event: Event) => {
      release();
      pending = {
        interaction,
        event,
        operation: null,
        timer: setTimeout(release, WRITE_WINDOW_MS),
      };
    },
    flush: release,
    dispose: () => {
      unsubscribe();
      release();
    },
  };
};
